/**
 * BASE CARD COMPONENT
 *
 * Reusable card wrapper with consistent styling.
 * Use this as the foundation for all cards in the app.
 *
 * Features:
 * - Dark/light variants
 * - Optional hover effects
 * - Clickable cards
 * - Multiple padding options
 *
 * @example
 * <BaseCard variant="light" hover onClick={() => openDetail(item)}>
 *   <img src={item.photo} />
 *   <h3>{item.name}</h3>
 * </BaseCard>
 */

import { type ReactNode, type HTMLAttributes, type KeyboardEvent, forwardRef } from 'react';

export type CardVariant = 'light' | 'dark' | 'subtle';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface BaseCardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card content */
  children: ReactNode;
  /** Color variant - dark uses glassmorphic styling */
  variant?: CardVariant;
  /** Whether card has hover effect */
  hover?: boolean;
  /** Whether card is clickable (adds cursor pointer) */
  clickable?: boolean;
  /** Padding size */
  padding?: CardPadding;
  /** Additional class names */
  className?: string;
}

const variantClasses: Record<CardVariant, string> = {
  light: 'bg-white/90 backdrop-blur-sm rounded-xl shadow-sm border border-gray-100',
  dark: 'glass-card',
  subtle: 'bg-white/80 backdrop-blur-sm rounded-xl border border-gray-200',
};

const hoverClasses: Record<CardVariant, string> = {
  light: 'hover:shadow-md transition-shadow',
  dark: 'hover:bg-slate-700/70 transition-colors',
  subtle: 'hover:shadow-sm hover:border-gray-300 transition-all',
};

const paddingClasses: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

export const BaseCard = forwardRef<HTMLDivElement, BaseCardProps>(
  (
    {
      children,
      variant = 'light',
      hover = false,
      clickable = false,
      padding = 'none',
      className = '',
      onClick,
      ...props
    },
    ref
  ) => {
    const isInteractive = clickable || !!onClick;

    const classes = [
      variantClasses[variant],
      hover ? hoverClasses[variant] : '',
      paddingClasses[padding],
      isInteractive ? 'cursor-pointer' : '',
      isInteractive ? 'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Handle keyboard activation for clickable cards
    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if ((e.key === 'Enter' || e.key === ' ') && onClick) {
        e.preventDefault();
        onClick(e as unknown as React.MouseEvent<HTMLDivElement>);
      }
    };

    return (
      <div
        ref={ref}
        className={classes}
        onClick={onClick}
        tabIndex={isInteractive ? 0 : undefined}
        role={isInteractive ? 'button' : undefined}
        onKeyDown={isInteractive ? handleKeyDown : undefined}
        {...props}
      >
        {children}
      </div>
    );
  }
);

BaseCard.displayName = 'BaseCard';

export default BaseCard;
